/**
 * useFolders Hook
 * React Hook für Desktop-Folder Management
 * Lädt/Speichert Ordner über Settings, synchronisiert über Events
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { folderService, type DesktopFolder } from '@/services/folder-service'
import type { IconPosition } from '@/types/settings'
import { logError } from '@/lib/logger'

export function useFolders() {
  const [folders, setFolders] = useState<Record<string, DesktopFolder>>({})
  const [isLoading, setIsLoading] = useState(true)
  // Tracks in-flight mutations so the 'settings-updated' handler stays quiet
  const isMutatingRef = useRef(false)

  /**
   * Lädt alle Ordner aus den Settings.
   * @param quiet – wenn true, wird isLoading NICHT auf true gesetzt (kein Flicker)
   */
  const loadFolders = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setIsLoading(true)
      const loadedFolders = await folderService.getFolders()
      setFolders(loadedFolders)
    } catch (error) {
      logError(error as Error, { context: 'useFolders.loadFolders' })
      setFolders({})
    } finally {
      if (!quiet) setIsLoading(false)
    }
  }, [])

  /**
   * Initial load + Event-Listener für Settings-Updates
   */
  useEffect(() => {
    loadFolders()

    const handleSettingsUpdate = () => {
      // Ignore event-triggered reloads while a mutation is in progress —
      // the mutation already applied an optimistic update.
      if (isMutatingRef.current) return
      loadFolders(true) // quiet: no loading spinner
    }

    window.addEventListener('settings-updated', handleSettingsUpdate)
    return () => {
      window.removeEventListener('settings-updated', handleSettingsUpdate)
    }
  }, [loadFolders])

  /**
   * Erstellt einen neuen Ordner – optimistisches Update, kein Flicker
   */
  const createFolder = useCallback(
    async (appIds: string[], position: IconPosition, name?: string): Promise<string> => {
      isMutatingRef.current = true
      // Optimistic: build a temporary folder object immediately
      const tempId = `folder-optimistic-${Date.now()}`
      const optimisticFolder: DesktopFolder = {
        id: tempId,
        name: name || 'Ordner',
        appIds: [...appIds],
        position,
        createdAt: Date.now(),
      }
      setFolders((prev) => ({ ...prev, [tempId]: optimisticFolder }))

      try {
        const realId = await folderService.createFolder(appIds, position, name)
        // Replace temp entry with real one (same data, correct id)
        setFolders((prev) => {
          const next = { ...prev }
          delete next[tempId]
          next[realId] = { ...optimisticFolder, id: realId }
          return next
        })
        return realId
      } catch (error) {
        // Rollback optimistic entry
        setFolders((prev) => {
          const next = { ...prev }
          delete next[tempId]
          return next
        })
        logError(error as Error, { context: 'useFolders.createFolder' })
        throw error
      } finally {
        isMutatingRef.current = false
      }
    },
    []
  )

  /**
   * Fügt eine App zu einem Ordner hinzu – optimistisches Update
   */
  const addAppToFolder = useCallback(
    async (folderId: string, appId: string): Promise<void> => {
      isMutatingRef.current = true
      // Optimistic
      setFolders((prev) => {
        const folder = prev[folderId]
        if (!folder || folder.appIds.includes(appId)) return prev
        return { ...prev, [folderId]: { ...folder, appIds: [...folder.appIds, appId] } }
      })

      try {
        await folderService.addAppToFolder(folderId, appId)
      } catch (error) {
        // Rollback
        setFolders((prev) => {
          const folder = prev[folderId]
          if (!folder) return prev
          return { ...prev, [folderId]: { ...folder, appIds: folder.appIds.filter((id) => id !== appId) } }
        })
        logError(error as Error, { context: 'useFolders.addAppToFolder', folderId, appId })
        throw error
      } finally {
        isMutatingRef.current = false
      }
    },
    []
  )

  /**
   * Entfernt eine App aus einem Ordner
   */
  const removeAppFromFolder = useCallback(
    async (folderId: string, appId: string, newPosition?: IconPosition): Promise<void> => {
      try {
        await folderService.removeAppFromFolder(folderId, appId, newPosition)
        await loadFolders(true)
      } catch (error) {
        logError(error as Error, { context: 'useFolders.removeAppFromFolder', folderId, appId })
        throw error
      }
    },
    [loadFolders]
  )

  /**
   * Benennt einen Ordner um
   */
  const renameFolder = useCallback(
    async (folderId: string, newName: string): Promise<void> => {
      try {
        await folderService.renameFolder(folderId, newName)
        await loadFolders(true)
      } catch (error) {
        logError(error as Error, { context: 'useFolders.renameFolder', folderId, newName })
        throw error
      }
    },
    [loadFolders]
  )

  /**
   * Löscht einen Ordner (Apps zurück auf Desktop)
   */
  const deleteFolder = useCallback(
    async (folderId: string): Promise<void> => {
      try {
        await folderService.deleteFolder(folderId)
        await loadFolders(true)
      } catch (error) {
        logError(error as Error, { context: 'useFolders.deleteFolder', folderId })
        throw error
      }
    },
    [loadFolders]
  )

  /**
   * Aktualisiert die Position eines Ordners
   */
  const updateFolderPosition = useCallback(
    async (folderId: string, position: IconPosition): Promise<void> => {
      try {
        await folderService.updateFolderPosition(folderId, position)
        // Optimistic update
        setFolders((prev) => {
          const folder = prev[folderId]
          if (!folder) return prev
          return {
            ...prev,
            [folderId]: {
              ...folder,
              position,
            },
          }
        })
      } catch (error) {
        logError(error as Error, { context: 'useFolders.updateFolderPosition', folderId })
        // Reload on error
        await loadFolders(true)
      }
    },
    [loadFolders]
  )

  /**
   * Prüft ob ein Ordner existiert
   */
  const folderExists = useCallback(
    (folderId: string): boolean => {
      return folderId in folders
    },
    [folders]
  )

  /**
   * Holt einen spezifischen Ordner
   */
  const getFolder = useCallback(
    (folderId: string): DesktopFolder | null => {
      return folders[folderId] || null
    },
    [folders]
  )

  /**
   * Prüft ob eine App in irgendeinem Ordner ist
   */
  const isAppInFolder = useCallback(
    (appId: string): boolean => {
      return Object.values(folders).some((folder) => folder.appIds.includes(appId))
    },
    [folders]
  )

  /**
   * Findet den Ordner, der eine bestimmte App enthält
   */
  const findFolderByAppId = useCallback(
    (appId: string): DesktopFolder | null => {
      const entry = Object.entries(folders).find(([_, folder]) => folder.appIds.includes(appId))
      return entry ? entry[1] : null
    },
    [folders]
  )

  return {
    folders,
    isLoading,
    createFolder,
    addAppToFolder,
    removeAppFromFolder,
    renameFolder,
    deleteFolder,
    updateFolderPosition,
    folderExists,
    getFolder,
    isAppInFolder,
    findFolderByAppId,
    refreshFolders: loadFolders,
  }
}
